import { Size } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import classNames from 'clsx';
import React, { useContext, useEffect, useRef, useState } from 'react';
import type Slider from 'react-slick';

import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';

import PrevIcon from '../../../assets/icons/chevron-left.svg';
import LearnMoreIcon from '../../../assets/icons/learn-more.svg';
import theme from '../../../themes';
import { Carousel } from '../../Carousel/Carousel';
import BaseHeader from '../BaseHeader/BaseHeader';

import { TXTCLI_Learn_More } from './HowItWorks.messages.json';
import styles from './HowItWorks.scss';

export interface HowItWorksSlide {
  carouselItem: JSX.Element;
  description: JSX.Element;
  showLink?: boolean;
}

interface Props {
  howItWorksLink: string;
  activeSlide: HowItWorksSlide[];
}

const HowItWorks: React.FC<Props> = ({ howItWorksLink, activeSlide }) => {
  const { setStyle } = useContext<ModalContextValue>(ModalContext);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider>(null);

  useEffect(() => {
    setStyle({
      closeOnOverlayClick: true,
      closeOnEsc: true,
      overlayClassName: classNames(theme.darkMode, styles.modalOverlay),
      className: classNames(styles.modalContainer, styles.modalContainerCentered),
      size: Size.MEDIUM
    });
  }, [setStyle]);

  return (
    <>
      <BaseHeader headerClassName={styles.header} />
      <Body className={styles.body}>
        <Carousel
          sliderRef={sliderRef}
          carouselSettings={{ infinite: false, dots: true }}
          currentSlideCallback={setCurrentSlide}
          previousIcon={<PrevIcon />}
          iconPosition="outside"
        >
          {activeSlide.map((slide, index) => (
            <div key={index} className={styles.slideWrapper}>
              {slide.carouselItem}
            </div>
          ))}
        </Carousel>

        <div className={styles.description}>{activeSlide[currentSlide]?.description}</div>
        <StarsLink
          href={howItWorksLink}
          className={classNames(
            styles.link,
            activeSlide[currentSlide]?.showLink && styles.linkVisible
          )}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Translation {...TXTCLI_Learn_More} tagName="span" />
          <LearnMoreIcon />
        </StarsLink>
      </Body>
    </>
  );
};

export default HowItWorks;
